import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Scalar helpers
 */
const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  error: "Must be a numeric string",
});

const UUIDSchema = z.uuid();

/**
 * Pagination schema mirroring common backend pattern
 */
export const APInvoicePaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

/**
 * Filters accepted by the list endpoint
 * All fields optional; server treats undefined as no filter
 */
export const ListAPInvoicesInputSchema = z.object({
  invoiceDateFrom: z.string().optional(),
  invoiceDateTo: z.string().optional(),
  dueDateFrom: z.string().optional(),
  dueDateTo: z.string().optional(),
  status: z.array(z.string()).optional(),
  paymentStatus: z.array(z.string()).optional(),
  supplierId: UUIDSchema.optional(),
  invoiceNumber: z.string().optional(),
  referenceId: z.string().optional(),
  minTotalAmount: DecimalStringSchema.optional(),
  maxTotalAmount: DecimalStringSchema.optional(),
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
  includeNumLineItemsMatched: z.boolean().optional(),
});

/**
 * Base invoice schema mirroring Sequelize attributes (sans line items)
 */
export const APInvoiceSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  supplierId: UUIDSchema.nullable().optional(),
  invoiceNumber: z.string().nullable().optional(),
  /** @deprecated Use referenceIds instead */
  referenceId: z.string().nullable().optional(),
  referenceIds: z.array(z.string()).nullable().optional(),
  invoiceDate: z.string(),
  receivedAt: z.string().nullable().optional(),
  dueDate: z.string(),
  subtotal: DecimalStringSchema,
  taxAmount: DecimalStringSchema.nullable().optional(),
  totalAmount: DecimalStringSchema,
  currency: z.string(),
  paymentTerms: z.string().nullable().optional(),
  paymentStatus: z.string(),
  paidAt: z.string().nullable().optional(),
  paidAmount: DecimalStringSchema.nullable().optional(),
  preferredPayDate: z.string().nullable().optional(),
  scheduledPaymentDate: z.string().nullable().optional(),
  scheduledPaymentExplanation: z.string().nullable().optional(),
  isVoided: z.boolean(),
  voidedAt: z.string().nullable().optional(),
  voidedByUserId: UUIDSchema.nullable().optional(),
  voidReason: z.string().nullable().optional(),
  paymentHold: z.boolean(),
  paymentHoldReason: z.string().nullable().optional(),
  paymentHoldByUserId: UUIDSchema.nullable().optional(),
  paymentHoldAt: z.string().nullable().optional(),
  status: z.string(),
  source: z.string().nullable().optional(),
  sourceId: UUIDSchema.nullable().optional(),
  glAccountId: UUIDSchema.nullable().optional(),
  costCenterId: UUIDSchema.nullable().optional(),
  departmentId: UUIDSchema.nullable().optional(),
  notes: z.string().nullable().optional(),
  internalNotes: z.string().nullable().optional(),
  originId: z.string().nullable().optional(),
  originSystem: z.string().nullable().optional(),
  originEntity: z.string().nullable().optional(),
  originSyncStatus: z.string().nullable().optional(),
  originSyncErrorMessage: z.string().nullable().optional(),
  originMetadata: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
  lineItemMatchSummary: z
    .object({
      matched: z.int().nonnegative(),
      total: z.int().nonnegative(),
    })
    .nullable()
    .optional(),
  hasDeliveryMatch: z.boolean().optional(),
});

export const ListAPInvoicesResponseSchema = z.object({
  invoices: z.array(APInvoiceSchema),
  pagination: APInvoicePaginationSchema,
});

export const GetAPInvoiceInputSchema = z.object({
  id: UUIDSchema,
});

export const APInvoiceLineItemSchema = z.object({
  id: UUIDSchema,
  lineNumber: z.int(),
  description: z.string().nullable().optional(),
  quantity: z.union([z.number(), z.string()]).nullable().optional(),
  unitPrice: DecimalStringSchema.nullable().optional(),
  totalPrice: DecimalStringSchema.nullable().optional(),
});

export const InboundEmailAttachmentSchema = z.object({
  id: UUIDSchema,
  fileName: z.string(),
  sizeBytes: z.number().nullable().optional(),
  mimeType: z.string().nullable().optional(),
  s3Url: z.string(),
  contentId: z.string().nullable().optional(),
});

export const InboundEmailSchema = z.object({
  id: UUIDSchema,
  conversationId: z.string().nullable().optional(),
  subject: z.string().nullable().optional(),
  from: z.string().nullable(),
  to: z.string(),
  cc: z.string().nullable().optional(),
  htmlContent: z.string().nullable().optional(),
  textContent: z.string().nullable().optional(),
  messageBodyHtml: z.string().nullable().optional(),
  attachments: z.array(InboundEmailAttachmentSchema).optional(),
  createdAt: z.coerce.date(),
});

export const GetAPInvoiceResponseSchema = z.object({
  invoice: APInvoiceSchema.nullable(),
  lineItems: z.array(APInvoiceLineItemSchema),
  inboundEmail: InboundEmailSchema.nullable(),
});

export const CreateVoucherInFamousInputSchema = z.object({
  apInvoiceId: UUIDSchema,
});

export const CreateVoucherInFamousResponseSchema = z.object({
  success: z.boolean(),
  originId: z.string().optional(),
  errorMessage: z.string().optional(),
});

export const UpdateAPInvoiceInputSchema = z.object({
  id: UUIDSchema,
  supplierId: UUIDSchema.nullable().optional(),
  /** @deprecated Use referenceIds instead */
  referenceId: z
    .string()
    .min(1, "Reference ID cannot be empty")
    .nullable()
    .optional(),
  referenceIds: z.array(z.string().min(1)).nullable().optional(),
  invoiceDate: z.string().optional(),
  dueDate: z.string().nullable().optional(),
  preferredPayDate: z.string().nullable().optional(),
  scheduledPaymentDate: z.string().nullable().optional(),
  scheduledPaymentExplanation: z.string().nullable().optional(),
});

export const UpdateAPInvoiceResponseSchema = APInvoiceSchema;

export const ApproveAPInvoiceInputSchema = z.object({
  id: UUIDSchema,
});

export const ApproveAPInvoiceResponseSchema = APInvoiceSchema;

export const DeleteAPInvoiceInputSchema = z.object({
  id: UUIDSchema,
});

export const DeleteAPInvoiceResponseSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
  deletedCounts: z
    .object({
      lineItems: z.int().nonnegative(),
      entityAssociations: z.int().nonnegative(),
      documentAssociations: z.int().nonnegative(),
      charges: z.int().nonnegative(),
      extractionResultsUnlinked: z.int().nonnegative(),
    })
    .optional(),
});

export const apInvoicesContracts = {
  list: oc
    .input(ListAPInvoicesInputSchema)
    .output(ListAPInvoicesResponseSchema)
    .route({
      method: "GET",
    }),
  get: oc
    .input(GetAPInvoiceInputSchema)
    .output(GetAPInvoiceResponseSchema)
    .route({
      method: "GET",
    }),
  update: oc
    .input(UpdateAPInvoiceInputSchema)
    .output(UpdateAPInvoiceResponseSchema)
    .route({
      method: "POST",
    }),
  createVoucherInFamous: oc
    .input(CreateVoucherInFamousInputSchema)
    .output(CreateVoucherInFamousResponseSchema)
    .route({
      method: "POST",
    }),
  approve: oc
    .input(ApproveAPInvoiceInputSchema)
    .output(ApproveAPInvoiceResponseSchema)
    .route({
      method: "POST",
    }),
  delete: oc
    .input(DeleteAPInvoiceInputSchema)
    .output(DeleteAPInvoiceResponseSchema)
    .route({
      method: "POST",
    }),
} as const;

export type APInvoice = z.infer<typeof APInvoiceSchema>;
export type APInvoiceLineItem = z.infer<typeof APInvoiceLineItemSchema>;
export type InboundEmail = z.infer<typeof InboundEmailSchema>;
export type InboundEmailAttachment = z.infer<
  typeof InboundEmailAttachmentSchema
>;
export type ListAPInvoicesInput = z.infer<typeof ListAPInvoicesInputSchema>;
export type ListAPInvoicesResponse = z.infer<
  typeof ListAPInvoicesResponseSchema
>;
export type GetAPInvoiceInput = z.infer<typeof GetAPInvoiceInputSchema>;
export type GetAPInvoiceResponse = z.infer<typeof GetAPInvoiceResponseSchema>;
export type CreateVoucherInFamousInput = z.infer<
  typeof CreateVoucherInFamousInputSchema
>;
export type CreateVoucherInFamousResponse = z.infer<
  typeof CreateVoucherInFamousResponseSchema
>;
export type UpdateAPInvoiceInput = z.infer<typeof UpdateAPInvoiceInputSchema>;
export type UpdateAPInvoiceResponse = z.infer<
  typeof UpdateAPInvoiceResponseSchema
>;
export type ApproveAPInvoiceInput = z.infer<typeof ApproveAPInvoiceInputSchema>;
export type ApproveAPInvoiceResponse = z.infer<
  typeof ApproveAPInvoiceResponseSchema
>;
export type DeleteAPInvoiceInput = z.infer<typeof DeleteAPInvoiceInputSchema>;
export type DeleteAPInvoiceResponse = z.infer<
  typeof DeleteAPInvoiceResponseSchema
>;
